import { tokenStore } from '../utils/token.js';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

async function request(method, path, body = null, options = {}) {
  const token = tokenStore.get();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : null,
    signal: options.signal,
  });
  if (!res.ok) {
    let errMsg = `HTTP ${res.status}`;
    try { const e = await res.json(); errMsg = e.detail || e.message || errMsg; } catch { /* ignore */ }
    const err = new Error(errMsg);
    err.status = res.status;
    throw err;
  }
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : res.text();
}

export const api = {
  get:    (path, opts)       => request('GET',    path, null, opts),
  post:   (path, body, opts) => request('POST',   path, body, opts),
  put:    (path, body, opts) => request('PUT',    path, body, opts),
  patch:  (path, body, opts) => request('PATCH',  path, body, opts),
  delete: (path, opts)       => request('DELETE', path, null, opts),
};

export const authApi = {
  loginUser:          (code)                    => api.post('/auth/login',               { code }),
  loginEmail:         (email, password)         => api.post('/auth/login-email',         { email, password }),
  loginStaff:         (identifier, password)    => api.post('/auth/staff-login',         { identifier, password }),
  setupAccount:       (data)                    => api.post('/auth/setup',               data),
  getProfile:         ()                        => api.get('/auth/me'),
  updateProfile:      (data)                    => api.put('/auth/me',                   data),
  changePassword:     (current, next)           => api.post('/auth/change-password',     { current_password: current, new_password: next }),
  getStaffList:       ()                        => api.get('/auth/staff-list'),
  resetStaffPassword: (staffId, new_password)   => api.post(`/auth/staff/${staffId}/reset-password`, { new_password }),
  resetUserPassword:  (userId,  new_password)   => api.post(`/auth/user/${userId}/reset-password`,   { new_password }),
};

export const checkinApi = {
  getWindow:    ()            => api.get('/checkin/window'),
  verifyUser:   (userId, day) => api.post('/checkin/verify',   { user_id: userId, day }),
  unverifyUser: (userId, day) => api.post('/checkin/unverify', { user_id: userId, day }),
  search:       (query)       => api.get(`/checkin/search?q=${encodeURIComponent(query)}`),
  getDayStats:  (day)         => api.get(`/checkin/stats?day=${day}`),
  exportDay:    (day)         => api.get(`/checkin/export?day=${day}`),
  openWindow:   (day)         => api.post('/checkin/window/open',  { day }),
  closeWindow:  (day)         => api.post('/checkin/window/close', { day }),
};

export const checkpointApi = {
  getActive:      ()                     => api.get('/checkpoint/active'),
  submit:         (checkpointId, answer) => api.post('/checkpoint/submit', { checkpoint_id: checkpointId, answer }),
  start:          (questionId)           => api.post('/checkpoint/start', { question_id: questionId }),
  stop:           ()                     => api.post('/checkpoint/stop'),
  getResults:     (checkpointId)         => api.get(`/checkpoint/results/${checkpointId}`),
  getQuestions:   ()                     => api.get('/checkpoint/questions'),
  createQuestion: (data)                 => api.post('/checkpoint/questions', data),
  updateQuestion: (id, data)             => api.put(`/checkpoint/questions/${id}`, data),
  deleteQuestion: (id)                   => api.delete(`/checkpoint/questions/${id}`),
  exportResults:  (checkpointId)         => api.get(`/checkpoint/results/${checkpointId}/export`),
};

export const chatApi = {
  listSessions:  ()           => api.get('/chat/sessions'),
  getSession:    (id)         => api.get(`/chat/sessions/${id}`),
  deleteSession: (id)         => api.delete(`/chat/sessions/${id}`),
};

export const testApi = {
  getStatus:      ()        => api.get('/test/status'),
  start:          ()        => api.post('/test/start'),
  stop:           ()        => api.post('/test/stop'),
  setSchedule:    (info)    => api.put('/test/schedule', { scheduled_info: info }),
  getQuestionsForUser: ()   => api.get('/test/questions/take'),
  submitAnswers:  (answers) => api.post('/test/submit', { answers }),
  getProgress:    ()        => api.get('/test/progress'),
  getMyResult:        ()        => api.get('/test/my-result'),
  getResultsSummary:  ()        => api.get('/test/results-summary'),
  getQuestions:       ()        => api.get('/test/questions'),
  createQuestion: (q)       => api.post('/test/questions', q),
  updateQuestion: (id, q)   => api.put(`/test/questions/${id}`, q),
  deleteQuestion: (id)      => api.delete(`/test/questions/${id}`),
};

export const infoApi = {
  list:   ()       => api.get('/info'),
  create: (data)   => api.post('/info', data),
  delete: (id)     => api.delete(`/info/${id}`),
};

export const adminApi = {
  getUsers:            ()       => api.get('/admin/users'),
  getCheckpointStats:    ()          => api.get('/admin/checkpoint-day-stats'),
  getUserSubmissions:    (userId)    => api.get(`/admin/users/${userId}/submissions`),
  getSessionRespondents: (sessionId) => api.get(`/admin/sessions/${sessionId}/respondents`),
  getTestResults:        ()          => api.get('/admin/test-results'),
  getUserTestAnswers:    (userId)    => api.get(`/admin/users/${userId}/test-answers`),
};
